// src/utils/generateAdminReportPDF.ts
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { formatDateGeorgian } from "./dateHelpers";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PDFAthleteEntry {
  name: string;
  dateOfBirth: string;
  category: string;
  division: string;
  weightCategory: string;
  price: number;
  coachEmail?: string;   // populated for bank-transfer / prepaid athletes
  isFree?: boolean;      // true = admin_manual (bank transfer)
  /** Explicit payment type — when present, takes priority over isFree. */
  paymentType?: "online" | "prepaid" | "admin_manual";
}

export interface PDFClubEntry {
  clubName: string;
  athletes: PDFAthleteEntry[];
  totalAmount: number;
}

export interface PDFCompetitionEntry {
  competitionName: string;
  dateRange: string;
  status: string;
  totalAthletes: number;
  totalClubs: number;
  totalRevenue: number;
  onlineRevenue?: number;
  bankTotal?: number;
  /** Revenue collected via credit packages (prepaid). */
  prepaidTotal?: number;
  clubs: PDFClubEntry[];
}

// ---------------------------------------------------------------------------
// Label dictionaries
// ---------------------------------------------------------------------------

const LABELS = {
  en: {
    reportTitle: "All Registrations — Full Report",
    orgName: "GEORGIAN TAEKWONDO FEDERATION",
    generated: "Generated:",
    competitions: "Competitions:",
    totalAthletes: "Total Athletes:",
    totalRevenue: "Total Revenue:",
    athletes: "athletes",
    clubs: "clubs",
    onlineRevenue: "Online",
    bankTotal: "Bank",
    prepaidTotal: "Prepaid",
    tableHeaders: [
      "#",
      "Athlete",
      "DOB",
      "Category",
      "Division",
      "Weight",
      "Price (GEL)",
      "Type",
    ],
    online: "Online",
    bank: "Bank",
    prepaid: "Prepaid",
    footer: "Georgian Taekwondo Federation — confidential administrative document",
    langTag: "EN",
  },
  ka: {
    reportTitle: "ყველა რეგისტრაცია — სრული ანგარიში",
    orgName: "საქართველოს ტაეკვონდოს ფედერაცია",
    generated: "გენ. თარიღი:",
    competitions: "შეჯიბრება:",
    totalAthletes: "სულ სპორტსმენი:",
    totalRevenue: "სულ შემოსავალი:",
    athletes: "სპორტსმენი",
    clubs: "კლუბი",
    onlineRevenue: "ონლაინ",
    bankTotal: "ბანკი",
    prepaidTotal: "წინასწარი",
    tableHeaders: [
      "#",
      "სპორტსმენი",
      "დ/ბ",
      "კატეგ.",
      "განყ.",
      "წონა",
      "ფასი (GEL)",
      "ტიპი",
    ],
    online: "ონლაინ",
    bank: "ბანკი",
    prepaid: "წინასწარი",
    footer:
      "საქართველოს ტაეკვონდოს ფედერაცია — კონფიდენციალური ადმინისტრაციული დოკუმენტი",
    langTag: "KA",
  },
};

// ---------------------------------------------------------------------------
// Private helpers
// ---------------------------------------------------------------------------

const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

const fetchAsDataURL = async (path: string): Promise<string | null> => {
  try {
    const res = await fetch(path);
    if (!res.ok) return null;
    const blob = await res.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

const registerGeorgianFont = async (doc: jsPDF): Promise<string> => {
  const FONT_NAME = "NotoSansGeorgian";
  const FONT_PATH = "/fonts/NotoSansGeorgian-Regular.ttf";
  try {
    const res = await fetch(FONT_PATH);
    if (!res.ok) {
      console.warn("[PDF] Georgian font not found – falling back to helvetica");
      return "helvetica";
    }
    const buffer = await res.arrayBuffer();
    const b64 = arrayBufferToBase64(buffer);
    doc.addFileToVFS("NotoSansGeorgian-Regular.ttf", b64);
    doc.addFont("NotoSansGeorgian-Regular.ttf", FONT_NAME, "normal");
    return FONT_NAME;
  } catch (e) {
    console.error("[PDF] Font registration error:", e);
    return "helvetica";
  }
};

/**
 * Resolves the human-readable payment type label for a single athlete row.
 * Priority: explicit paymentType field > legacy isFree boolean.
 */
const resolveAthleteTypeLabel = (
  a: PDFAthleteEntry,
  L: (typeof LABELS)["en"]
): string => {
  const pt = a.paymentType;

  if (pt === "prepaid") {
    return a.coachEmail ? `${L.prepaid} — ${a.coachEmail}` : L.prepaid;
  }
  if (pt === "admin_manual" || a.isFree) {
    return a.coachEmail ? `${L.bank} — ${a.coachEmail}` : L.bank;
  }
  return L.online;
};

// ---------------------------------------------------------------------------
// Page renderer — one full report section per language
// ---------------------------------------------------------------------------

function renderReportSection(
  doc: jsPDF,
  entries: PDFCompetitionEntry[],
  logoDataUrl: string | null,
  activeFont: string,
  lang: "en" | "ka",
  startY: number
): void {
  const L = LABELS[lang];
  const PAGE_W = 210;
  const MARGIN = 14;
  const RED: [number, number, number] = [218, 41, 28];
  const LIGHT_GRAY: [number, number, number] = [241, 245, 249];
  const MID_GRAY: [number, number, number] = [100, 116, 139];
  const DARK: [number, number, number] = [15, 23, 42];
  const WHITE: [number, number, number] = [255, 255, 255];

  // ── Cover header ──────────────────────────────────────────────────────
  doc.setFillColor(...RED);
  doc.rect(0, 0, PAGE_W, 34, "F");

  if (logoDataUrl) {
    doc.addImage(logoDataUrl, "PNG", MARGIN, 5, 22, 22);
  }

  doc.setTextColor(...WHITE);
  doc.setFont(activeFont, "normal");
  doc.setFontSize(13);
  doc.text(L.orgName, logoDataUrl ? 40 : MARGIN, 15);
  doc.setFontSize(9);
  doc.text(L.reportTitle, logoDataUrl ? 40 : MARGIN, 25);

  // Language tag
  doc.setFontSize(8);
  doc.text(L.langTag, PAGE_W - MARGIN, 10, { align: "right" });

  // ── Summary block ─────────────────────────────────────────────────────
  const today = new Date();
  const todayStr =
    lang === "ka"
      ? formatDateGeorgian(today)
      : today.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });

  const totalAthletes = entries.reduce((s, e) => s + e.totalAthletes, 0);
  const totalRevenue = entries.reduce((s, e) => s + e.totalRevenue, 0);

  doc.setTextColor(...DARK);
  doc.setFont(activeFont, "normal");
  doc.setFontSize(8.5);
  doc.text(`${L.generated} ${todayStr}`, MARGIN, 42);
  doc.text(
    `${L.competitions} ${entries.length}   ·   ${L.totalAthletes} ${totalAthletes}   ·   ${L.totalRevenue} ${totalRevenue} GEL`,
    MARGIN,
    50
  );

  let y = 60;

  // ── Competition sections ──────────────────────────────────────────────
  for (const comp of entries) {
    if (y > 240) {
      doc.addPage();
      y = 16;
    }

    // Derive revenue breakdown — fall back to totals if not supplied
    const onlineRev = comp.onlineRevenue ?? comp.totalRevenue;
    const bankRev = comp.bankTotal ?? 0;
    const prepaidRev = comp.prepaidTotal ?? 0;

    // Competition panel
    doc.setFillColor(...LIGHT_GRAY);
    doc.rect(MARGIN, y, PAGE_W - MARGIN * 2, 20, "F");
    doc.setFillColor(...RED);
    doc.rect(MARGIN, y, 3, 20, "F");

    doc.setFont(activeFont, "normal");
    doc.setFontSize(11);
    doc.setTextColor(...DARK);
    doc.text(comp.competitionName, MARGIN + 7, y + 7);

    doc.setFontSize(8);
    doc.setTextColor(...MID_GRAY);
    doc.text(
      `${comp.dateRange}  ·  ${comp.status.toUpperCase()}`,
      MARGIN + 7,
      y + 14
    );

    // Revenue summary in comp header (online / bank / prepaid)
    doc.setFontSize(8);
    doc.setTextColor(...DARK);
    doc.text(
      [
        `${comp.totalAthletes} ${L.athletes}`,
        `${comp.totalClubs} ${L.clubs}`,
        `${L.onlineRevenue}: ${onlineRev} GEL`,
        `${L.bankTotal}: ${bankRev} GEL`,
        `${L.prepaidTotal}: ${prepaidRev} GEL`,
      ].join("  ·  "),
      PAGE_W - MARGIN,
      y + 11,
      { align: "right" }
    );

    y += 24;

    // ── Club sections ──────────────────────────────────────────────────
    for (const club of comp.clubs) {
      if (y > 255) {
        doc.addPage();
        y = 16;
      }

      // Club label
      doc.setFillColor(248, 250, 252);
      doc.rect(MARGIN + 4, y, PAGE_W - MARGIN * 2 - 4, 9, "F");

      doc.setFont(activeFont, "normal");
      doc.setFontSize(9);
      doc.setTextColor(...RED);
      doc.text(club.clubName, MARGIN + 9, y + 6);

      doc.setTextColor(...MID_GRAY);
      doc.text(
        `${club.athletes.length} ${L.athletes}  ·  ${club.totalAmount} GEL`,
        PAGE_W - MARGIN,
        y + 6,
        { align: "right" }
      );

      y += 11;

      // Athlete table
      autoTable(doc, {
        startY: y,
        margin: { left: MARGIN + 4, right: MARGIN },
        head: [L.tableHeaders],
        body: club.athletes.map((a, i) => [
          i + 1,
          a.name,
          a.dateOfBirth,
          a.category,
          a.division,
          a.weightCategory,
          `${a.price}`,
          resolveAthleteTypeLabel(a, L),
        ]),
        headStyles: {
          fillColor: LIGHT_GRAY,
          textColor: MID_GRAY,
          fontStyle: "normal",
          font: activeFont,
          fontSize: 7.5,
        },
        bodyStyles: {
          font: activeFont,
          fontSize: 7.5,
          textColor: DARK,
        },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        styles: {
          font: activeFont,
          fontSize: 7.5,
          cellPadding: 2,
          overflow: "linebreak",
        },
        columnStyles: {
          0: { cellWidth: 7 },
          2: { cellWidth: 20 },
          3: { cellWidth: 16 },
          4: { cellWidth: 16 },
          5: { cellWidth: 18 },
          6: { cellWidth: 20 },
          7: { cellWidth: 30 },
        },
      });

      y = (doc as any).lastAutoTable.finalY + 7;
    }

    y += 6;
  }

  // ── Footer on last page ───────────────────────────────────────────────
  doc.setFont(activeFont, "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(180, 180, 180);
  doc.text(L.footer, MARGIN, 287);
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

export async function generateAdminReportPDF(
  entries: PDFCompetitionEntry[]
): Promise<void> {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const activeFont = await registerGeorgianFont(doc);
  const logoDataUrl = await fetchAsDataURL("/geo.png");

  // ── English section ───────────────────────────────────────────────────
  renderReportSection(doc, entries, logoDataUrl, activeFont, "en", 0);

  // ── Georgian section (starts on a new page) ───────────────────────────
  doc.addPage();
  renderReportSection(doc, entries, logoDataUrl, activeFont, "ka", 0);

  const filename = `full-report-${new Date().toISOString().slice(0, 10)}.pdf`;
  doc.save(filename);
  console.log(`[PDF] Saved ${filename}`);
}